/**
 * 대본 책.
 *
 * 배역 대본은 **읽고 외우는 물건**이다. 90분 게임에서 앞에 20분을 여기 쓴다.
 * 그런데 지금까지는 조사 결과를 띄우는 것과 같은 시트에 얹혀 있었다 —
 * 12.5px 산세리프에 줄바꿈이 `<br>` 로 붙은 회색 상자. 그걸로 3,000자를
 * 외우라는 것은 무리다.
 *
 * 그래서 **책으로 만든다.**
 *   · 두 쪽 펼침. 종이 폭이 좁아야 눈이 줄을 안 놓친다 (한 줄 32~38자)
 *   · 세리프. 한국어 장문은 명조가 눈이 덜 아프다
 *   · 본문 16px / 행간 1.9 — 화면에서 읽는 글은 종이보다 커야 한다
 *   · 탭으로 가른다. 대본 · 인물 · 수첩 · 반론책
 *
 * **폰트는 안 받아 온다.** 외부 에셋 0개가 이 프로젝트의 규칙이고,
 * 한글 웹폰트 하나가 2MB 다. 대신 기기에 이미 있는 명조를 순서대로 찾는다 —
 * 애플 기기는 Apple SD Gothic 대신 AppleMyungjo, 윈도우는 바탕,
 * 안드로이드는 Noto Serif CJK. 없으면 serif 로 떨어진다.
 */

import { CAST, CAST_BY_ID, type CastDef } from '../../shared/cast.gen';

export interface BookScript {
  castId: string;
  culprit: boolean;
  sections: { title: string; body: string }[];
}

/** 수첩 한 줄. 조사해서 알게 된 것이 여기 쌓인다 */
export interface NoteLine {
  /** 무엇으로 알게 됐나 */
  clueId: string;
  /** 한 줄 요약. 없으면 단서 제목 */
  text: string;
  /** 12/17 03:10 처럼 시각이 있으면 타임라인에 꽂힌다 */
  at?: string;
  /** 사실 / 진술 / 시각 */
  kind: 'fact' | 'said' | 'time';
  /** 누가 찾았나 */
  by?: string;
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * 본문 조판.
 *
 * 대본에는 세 가지가 섞여 있다 — 산문, ``` 로 감싼 시각표, `>` 인용.
 * 셋을 다르게 그려야 눈이 구조를 잡는다. 특히 시각표는 **등폭**이어야
 * 시각이 세로로 줄을 맞춘다. 그게 안 맞으면 타임라인으로 안 읽힌다.
 */
function typeset(body: string): string {
  const out: string[] = [];
  let mode: 'p' | 'pre' | 'quote' = 'p';
  let buf: string[] = [];

  const flush = () => {
    if (!buf.length) return;
    const t = buf.join('\n');
    // 시각표 안에서도 `**굵게**` 는 살린다. 안 그러면 별표가 그대로 보인다.
    // <b> 는 등폭 폭을 안 바꾸므로 세로 정렬이 유지된다
    if (mode === 'pre') out.push(`<pre class="tl">${md(esc(t))}</pre>`);
    else if (mode === 'quote') out.push(`<blockquote>${md(esc(t)).replace(/\n/g, '<br>')}</blockquote>`);
    else
      for (const para of t.split(/\n{2,}/)) {
        if (!para.trim()) continue;
        const per = person(para);
        // esc 를 먼저 걸고 reflow 가 <br> 를 넣는다. 순서가 반대면 내가 넣은
        // 개행이 esc 에 잡아먹혀 대사가 한 줄에 뭉친다
        out.push(per ?? `<p>${md(reflow(esc(para)))}</p>`);
      }
    buf = [];
  };

  for (const line of body.split('\n')) {
    if (line.trim().startsWith('```')) {
      flush();
      mode = mode === 'pre' ? 'p' : 'pre';
      continue;
    }
    if (mode === 'pre') {
      buf.push(line);
      continue;
    }
    const q = line.startsWith('>');
    if (q !== (mode === 'quote')) {
      flush();
      mode = q ? 'quote' : 'p';
    }
    buf.push(q ? line.replace(/^>\s?/, '') : line);
  }
  flush();
  return out.join('');
}

/**
 * 사람 한 명. `- 한서진 (33, 대표) — 작년에 참고인으로 만났다…` 꼴이면
 * 문단이 아니라 **카드**로 그린다. 그냥 흘려 쓰면 이름 여섯이 글 벽이 된다.
 */
function person(para: string): string | null {
  const m = /^-\s*([^(]+)\((\d+)\s*,\s*([^)]+)\)\s*[—–-]\s*([\s\S]+)$/.exec(para.trim());
  if (!m) return null;
  const [, name, age, job, body] = m;
  return `<div class="pers">
    <div class="pers-h"><b>${esc(name.trim())}</b><span>${age} · ${esc(job.trim())}</span></div>
    <div class="pers-b">${md(esc(body.trim())).replace(/\n/g, '<br>')}</div>
  </div>`;
}

/** `**굵게**` 만. 대본에 쓰는 표시는 이것 하나뿐이다 */
const md = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');

/**
 * 문단 안의 줄바꿈을 정리한다.
 *
 * 원본 마크다운은 읽기 좋으라고 90자쯤에서 접어 뒀다. 그걸 그대로 <br> 로
 * 바꾸는 바람에 「47개 / 지점의」처럼 어중간한 데서 끊겼다. 그래서 이어 붙이는데,
 * **대사와 목록은 이어 붙이면 안 된다** — 「거기 내려가서 뭐 하려고.」 「제 이름으로
 * 하려고요.」가 한 줄에 뭉쳐 누가 말하는지가 사라진다. 그 줄만 개행을 지킨다.
 */
const KEEP = /^\s*(?:[「『]|[-·•]\s|\d+[.)]\s)/;
function reflow(para: string): string {
  const lines = para.split('\n');
  let out = lines[0] ?? '';
  for (let i = 1; i < lines.length; i++) {
    const cur = lines[i];
    const prev = lines[i - 1];
    // 이번 줄이 대사·목록으로 시작하거나, 앞줄이 대사로 끝났으면 줄을 바꾼다
    const brk = KEEP.test(cur) || /[」』]\s*$/.test(prev);
    out += (brk ? '\n' : ' ') + cur.trim();
  }
  return out.replace(/\n/g, '<br>');
}

/* ------------------------------------------------------------------ */

export type BookTab = 'brief' | 'script' | 'map' | 'cast' | 'note' | 'rebut';

export interface BookState {
  tab: BookTab;
  /** 대본에서 지금 펼친 쪽 (두 절씩 넘긴다) */
  page: number;
  /** 인물 탭에서 펼쳐 본 사람. 없으면 목록 */
  open?: string;
}

/** 인물마다 내가 적어 둔 것. 저장은 부르는 쪽이 한다 */
export type Memos = Record<string, string>;

export function renderBook(
  st: BookState,
  mine: BookScript | null,
  playerOfCast: Record<string, string>,
  notes: NoteLine[],
  memos: Memos = {},
): string {
  const myCast = mine ? CAST_BY_ID[mine.castId] : null;
  const tabs = (
    [
      ['brief', '요약'],
      ['script', '대본'],
      ['map', '지도'],
      ['cast', '인물'],
      ['note', '수첩'],
      ['rebut', '반론책'],
    ] as [BookTab, string][]
  )
    .filter(([k]) => k !== 'rebut' || (mine && myCast?.role !== 'detective'))
    .map(
      ([k, label]) =>
        `<button class="btab ${st.tab === k ? 'on' : ''}" data-tab="${k}">${label}</button>`,
    )
    .join('');

  let body = '';
  if (st.tab === 'brief') body = briefPages(mine, myCast);
  else if (st.tab === 'script') body = scriptPages(st, mine, myCast);
  else if (st.tab === 'map') body = mapPages(myCast);
  else if (st.tab === 'cast') body = castPages(st, playerOfCast, myCast, notes, memos);
  else if (st.tab === 'note') body = notePages(notes);
  else body = rebutPages(mine);

  return `<div class="book">
    <div class="btabs">${tabs}</div>
    <div class="spread">${body}</div>
  </div>`;
}

/** 지도 — 공용 배치 한 장 + 자기 동선 한 장.
 *
 *  **자기 것만 보여준다.** 남의 동선이 보이면 이 판이 통째로 무너진다.
 *  시각과 「누구를 봤나」는 여기 안 적는다 — 그건 대본 「십육일 밤 내 행적」이
 *  전담한다. 그림의 글자는 못 고치고, 두 군데가 서로 다른 말을 하게 되기 때문이다. */
function mapPages(myCast: CastDef | null): string {
  if (!myCast) return `<div class="page"><div class="empty">아직 배역을 못 받았습니다.</div></div>`;
  return `<div class="page">
    <div class="phead">백설리</div>
    <img class="bmap" src="/maps/base.jpg" alt="마을 지도">
    <div class="bhint">건물과 걸리는 시간. 전원이 같은 것을 본다</div>
  </div>
  <div class="page">
    <div class="phead">그날 밤 내가 간 길</div>
    <img class="bmap" src="/maps/${myCast.id}.jpg" alt="내 동선">
    <div class="bhint">나만 본다. 시각은 대본 「십육일 밤 내 행적」에 있다</div>
  </div>`;
}

/** 요약 — 판이 도는 중에 흘깃 보는 쪽.
 *
 *  **대본을 소스로 삼는다.** 따로 적어 두면 대본을 고칠 때 이쪽이 남아
 *  두 군데가 서로 다른 말을 하게 된다. 그 사고를 시나리오 문서에서 이미 겪었다.
 *
 *  시각은 행적 절에서 「HH:MM 으로 시작하는 줄」만 긁는다. 대본이 바뀌면
 *  요약도 같이 바뀐다. */
const BRIEF: { head: string; from: string[]; times?: boolean; hint?: string }[] = [
  { head: '내 차례에 할 것', from: ['내 목적과 초반 포인트'] },
  { head: '그날 밤 내 시각', from: ['십육일 밤 내 행적'], times: true,
    hint: '이 시각만은 안 틀려야 합니다' },
  { head: '내가 숨기는 것', from: ['내가 숨기는 것'], hint: '먼저 말할 의무는 없습니다' },
  { head: '나만 아는 한 가지', from: ['나만 아는 한 가지'], hint: '언제 꺼낼지는 내가 정합니다' },
];

/** 「01:20  들어갔다」처럼 시각으로 시작하는 줄만 남긴다 */
function timeLines(body: string): string {
  const rows = body
    .split('\n')
    .map((l) => l.replace(/^[>\s`|-]+/, '').trimEnd())
    .filter((l) => /^\d{1,2}:\d{2}\s+\S/.test(l));
  if (!rows.length) return '';
  return `<ul class="btimes">${rows
    .map((l) => {
      const at = l.slice(0, 5);
      return `<li><b>${esc(at)}</b>${esc(l.slice(5).trim())}</li>`;
    })
    .join('')}</ul>`;
}

function briefPages(mine: BookScript | null, myCast: CastDef | null): string {
  if (!mine || !myCast) {
    return `<div class="page"><div class="empty">아직 배역을 못 받았습니다.</div></div>`;
  }
  const pick = (names: string[]) =>
    mine.sections.find((s) => names.some((n) => s.title.includes(n)));

  const cards = BRIEF.map(({ head, from, times, hint }) => {
    const sec = pick(from);
    if (!sec) return '';
    const inner = times ? timeLines(sec.body) : typeset(sec.body);
    if (!inner) return '';
    return `<section class="bcard">
      <h4>${esc(head)}${hint ? `<em>${esc(hint)}</em>` : ''}</h4>
      ${inner}
    </section>`;
  })
    .filter(Boolean)
    .join('');

  const flag = `<div class="bflag ${mine.culprit ? 'cul' : ''}">${
    mine.culprit
      ? '당신이 범인입니다. 오직 당신만 거짓말을 할 수 있습니다.'
      : '당신은 범인이 아닙니다. 거짓말은 못 하지만 침묵할 수는 있습니다.'
  }</div>`;

  // 요약은 넘기지 않는다. 한 화면에 다 보여야 흘깃 볼 수 있다
  return `<div class="page bbrief">
      <div class="bhead">
        <div class="bname">${esc(myCast.name)}</div>
        <div class="bsub">${myCast.age} · ${esc(myCast.job)}</div>
        ${flag}
      </div>
      ${cards}
      <div class="bnote">전문은 <b>대본</b> 탭에 있습니다</div>
    </div>`;
}

/** 대본 — 두 쪽에 절 하나씩. 넘기면 다음 둘 */
function scriptPages(st: BookState, mine: BookScript | null, myCast: CastDef | null): string {
  if (!mine || !myCast) {
    return `<div class="page"><div class="empty">아직 배역을 못 받았습니다.<br>호스트가 배역을 나눌 때까지 기다립니다.</div></div>`;
  }
  // **읽는 순서를 다시 잡는다.**
  // 원본 순서는 「이 판의 규칙」이 맨 앞인데, 책을 펴자마자 거짓말 규칙을 읽으면
  // 첫인상이 설명서가 된다. 베르단디도 「당신의 이야기」가 첫 장이고 규칙은 뒤였다.
  // 규칙과 변론표는 언제든 탭으로 볼 수 있으므로 뒤로 보낸다.
  const BACK = ['이 판의 규칙', '단서를 들이대면'];
  const secs = [
    ...mine.sections.filter((s) => !BACK.some((b) => s.title.includes(b))),
    ...mine.sections.filter((s) => BACK.some((b) => s.title.includes(b))),
  ];
  const i = Math.max(0, Math.min(st.page, Math.ceil(secs.length / 2) - 1));
  const L = secs[i * 2];
  const R = secs[i * 2 + 1];
  const total = Math.ceil(secs.length / 2);

  // 표제는 **첫 쪽 안**에 넣는다. 절대배치로 띄우면 본문 첫 줄을 덮는다
  const head =
    i === 0
      ? `<div class="bhead">
           <div class="bname">${esc(myCast.name)}</div>
           <div class="bsub">${myCast.age} · ${esc(myCast.job)}${
             myCast.role === 'detective' ? ' <em class="bdet">탐정</em>' : ''
           }</div>
           <div class="bflag ${mine.culprit ? 'cul' : ''}">${
             mine.culprit
               ? '당신이 범인입니다. 오직 당신만 거짓말을 할 수 있습니다.'
               : '당신은 범인이 아닙니다. 거짓말은 못 하지만 침묵할 수는 있습니다.'
           }</div>
         </div>`
      : '';

  const pg = (s: { title: string; body: string } | undefined, n: number, top = '') =>
    s
      ? `<div class="page">${top}<h3>${esc(s.title)}</h3>${typeset(s.body)}<div class="pnum">${n}</div></div>`
      : `<div class="page blank">${top}<div class="pnum">${n}</div></div>`;

  // 넘김은 **가장자리**에 둔다. 올리면 종이가 살짝 들리고 화살표가 뜬다
  const edge = (dir: -1 | 1, on: boolean) =>
    on
      ? `<button class="bedge ${dir < 0 ? 'prev' : 'next'}" data-page="${i + dir}"
                aria-label="${dir < 0 ? '앞 장' : '다음 장'}">
           <span class="bedge-arrow">${dir < 0 ? '‹' : '›'}</span>
           <span class="bedge-num">${i + dir + 1} / ${total}</span>
         </button>`
      : '';

  return `${pg(L, i * 2 + 1, head)}${pg(R, i * 2 + 2)}
    ${edge(-1, i > 0)}${edge(1, i < total - 1)}
    <div class="bfold">${i + 1} / ${total}</div>`;
}

/** 인물 — 여섯을 언제든 본다. **외울 필요가 없어야 대본이 가벼워진다** */
function castPages(
  st: BookState,
  playerOfCast: Record<string, string>,
  myCast: CastDef | null,
  notes: NoteLine[],
  memos: Memos,
): string {
  if (st.open) {
    const c = CAST_BY_ID[st.open];
    if (c) return profilePage(c, playerOfCast[c.id], myCast?.id === c.id, notes, memos[c.id] ?? '');
  }
  const cards = CAST.filter((c) => c.role !== 'victim')
    .map((c) => {
      const who = playerOfCast[c.id];
      const me = myCast?.id === c.id;
      // 신체는 「성별 여 · 나이 33 · 키 163 …」 꼴이라 칩으로 끊어야 읽힌다
      const chips = c.body
        .split('\n')
        .flatMap((l) => l.split('·'))
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => `<span>${esc(v)}</span>`)
        .join('');
      return `<button class="pcard ${me ? 'me' : ''} ${c.role === 'detective' ? 'det' : ''}" data-cast-open="${c.id}">
        <div class="pface">
          <img src="/cards/${c.id}.png" alt="" loading="lazy"
               onerror="this.style.display='none';this.parentElement.classList.add('noart')" />
          <span class="pface-i">${esc(c.name.slice(0, 1))}</span>
        </div>
        <div class="pinfo">
        <div class="pcard-top">
          <div class="pcard-id">
            <div class="pname">${esc(c.name)}${
              c.role === 'detective' ? '<i class="pdet">탐정</i>' : ''
            }</div>
            <div class="pjob">${c.age} · ${esc(c.job)}</div>
          </div>
          ${me ? '<b class="ptag mine">나</b>' : who ? `<b class="ptag">${esc(who)}</b>` : ''}
        </div>
        <div class="pchips">${chips}</div>
        <p>${md(esc(c.public)).replace(/\n/g, '<br>')}</p>
        ${c.look ? `<p class="plook">${md(esc(c.look)).replace(/\n/g, '<br>')}</p>` : ''}
        </div>
      </button>`;
    })
    .join('');
  const victim = CAST.find((c) => c.role === 'victim');
  return `<div class="page wide cast">
    <h3>이 자리에 있는 사람들</h3>
    <p class="lead">남에 대해서는 <b>보이는 것까지만</b> 압니다. 왜 그런지는 그 사람 대본에만 있습니다.</p>
    <div class="pgrid">${cards}${
      victim
        ? `<div class="pcard vic">
             <div class="pface">
               <img src="/cards/${victim.id}.png" alt="" loading="lazy"
                    onerror="this.style.display='none';this.parentElement.classList.add('noart')" />
               <span class="pface-i">${esc(victim.name.slice(0, 1))}</span>
             </div>
             <div class="pinfo">
             <div class="pcard-top">
               <div class="pcard-id">
                 <div class="pname">${esc(victim.name)}<i class="pvic">피해자</i></div>
                 <div class="pjob">${victim.age} · 대표</div>
               </div>
             </div>
             <div class="pchips">${victim.body
               .split('\n')
               .flatMap((l) => l.split('·'))
               .map((v) => v.trim())
               .filter(Boolean)
               .map((v) => `<span>${esc(v)}</span>`)
               .join('')}</div>
             <p>${md(esc(victim.public)).replace(/\n/g, '<br>')}</p>
             </div>
           </div>`
        : ''
    }</div>
  </div>`;
}

/**
 * 한 사람 프로파일. **왼쪽 얼굴, 오른쪽 아는 것.**
 *
 * 아래 두 칸이 나뉘어 있다 —
 *   조사로 알게 된 것   앱이 적는다. 신발 사이즈처럼 확정된 사실
 *   내가 적은 것        사람이 적는다. 추리와 의심은 자동으로 안 나온다
 *
 * 사람 플레이테스트에서 「네 번째 브리핑에서 첫 번째를 잊는다」가 나왔다.
 * 기억을 앱이 대신 하되, 판단은 사람이 한다.
 */
function profilePage(
  c: CastDef,
  who: string | undefined,
  isMe: boolean,
  notes: NoteLine[],
  memo: string,
): string {
  const mine = notes.filter((x) => x.by === c.id || x.text.includes(c.name));
  // **마주 서면 누구나 보이는 것이다.** 조사로 알아내는 게 아니라
  // 처음부터 적혀 있어야 한다 — 키·체격·머리·손은 눈에 그냥 들어온다.
  // 예전에는 「여」 「31」 「165」 하는 식으로 라벨 없이 흩뿌려 놨다
  const LABELS = ['성별', '나이', '키', '체격', '머리'];
  const rows: [string, string][] = [];
  for (const line of c.body.split('\n')) {
    for (const part of line.split('·')) {
      const t = part.trim();
      if (!t) continue;
      const k = LABELS.find((l) => t.startsWith(l));
      if (k) rows.push([k, t.slice(k.length).trim()]);
      else if (rows.length) rows[rows.length - 1][1] += ` · ${t}`;
    }
  }
  const basic = rows.length
    ? `<dl class="pbasic">${rows.map(([k, v]) => `<dt>${k}</dt><dd>${esc(v)}</dd>`).join('')}</dl>`
    : '';

  return `<div class="page prof-l">
      <button class="prof-back" data-cast-open="">‹ 여섯 명 전부 보기</button>
      <div class="prof-face">
        <img src="/cards/${c.id}.png" alt="" onerror="this.style.display='none';this.parentElement.classList.add('noart')" />
        <span class="prof-i">${esc(c.name.slice(0, 1))}</span>
      </div>
      <div class="prof-name">${esc(c.name)}${
        c.role === 'detective' ? '<i class="pdet">탐정</i>' : c.role === 'victim' ? '<i class="pvic">피해자</i>' : ''
      }</div>
      <div class="prof-job">${c.age} · ${esc(c.job)}</div>
      ${isMe ? '<div class="prof-me">내 배역입니다</div>' : who ? `<div class="prof-who">${esc(who)} 님</div>` : ''}
    </div>
    <div class="page prof-r">
      <h3>기본</h3>
      ${basic}

      <h3>보이는 것</h3>
      <p>${md(esc(c.public)).replace(/\n/g, '<br>')}</p>
      ${c.look ? `<p class="plook">${md(esc(c.look)).replace(/\n/g, '<br>')}</p>` : ''}

      <h3>조사로 알게 된 것</h3>
      ${
        mine.length
          ? `<ul class="notes">${mine
              .map((x) => `<li class="k-${x.kind}">${x.at ? `<b>${esc(x.at)}</b> ` : ''}${esc(x.text)}</li>`)
              .join('')}</ul>`
          : '<p class="prof-empty">아직 없습니다. 조사하면 여기 쌓입니다 — 신발 사이즈처럼 확정된 것만.</p>'
      }

      <h3>내가 적은 것</h3>
      <textarea class="prof-memo" data-memo="${c.id}" rows="8"
        placeholder="들은 말, 어긋난 시각, 의심. 나만 봅니다.">${esc(memo)}</textarea>
    </div>`;
}

/**
 * 수첩 — 조사해서 알게 된 것이 쌓인다.
 *
 * 그리고 **시각이 있는 것은 시간표에 꽂힌다.** 지금 단서 일흔 개에 시각이
 * 스물세 개 박혀 있는데 모을 데가 없어서 사람이 머릿속으로 맞춰야 했다.
 * 90분 게임에서 그건 안 된다.
 */
function notePages(notes: NoteLine[]): string {
  if (!notes.length) {
    return `<div class="page wide"><div class="empty">아직 아무것도 못 봤습니다.<br>마을을 돌아다니며 조사하면 여기 쌓입니다.</div></div>`;
  }
  const timed = notes.filter((n) => n.at).sort((a, b) => (a.at! < b.at! ? -1 : 1));
  const plain = notes.filter((n) => !n.at);

  const tl = timed.length
    ? `<h3>시간표</h3>
       <div class="tlwrap">${timed
         .map(
           (n) => `<div class="tlrow"><span class="tlat">${esc(n.at!)}</span>
             <span class="tltx">${esc(n.text)}</span></div>`,
         )
         .join('')}</div>`
    : '';

  const list = plain.length
    ? `<h3>알게 된 것</h3>
       <ul class="notes">${plain
         .map((n) => `<li class="k-${n.kind}">${esc(n.text)}</li>`)
         .join('')}</ul>`
    : '';

  return `<div class="page wide">${tl}${list}</div>`;
}

/**
 * 반론책 — 내 단서별 변론.
 *
 * 평소에는 여기서 찾아보고, 토론 중에 내 단서가 칠판에 걸리면
 * 그 항목만 따로 띄운다. 탐정은 이 책이 없다 — 변론할 것이 없기 때문이다.
 */
function rebutPages(mine: BookScript | null): string {
  const sec = mine?.sections.find((s) => s.title.includes('단서를 들이대면'));
  if (!sec) {
    return `<div class="page wide"><div class="empty">이 배역에는 반론책이 없습니다.</div></div>`;
  }
  const items = sec.body
    .split(/\n{2,}/)
    .map((blk) => {
      const [head, ...rest] = blk.split('\n');
      return `<div class="reb"><div class="rq">${esc(head)}</div>
        <div class="ra">${md(esc(rest.join(' ').replace(/^\s*->\s*/, '')))}</div></div>`;
    })
    .join('');
  return `<div class="page wide">
    <h3>단서를 들이대면</h3>
    <p class="lead">여기 적힌 변론을 <b>크게 벗어나는 말은 못 합니다.</b>
      즉석에서 지어내면 범인이 아닌 사람도 거짓말을 하게 되고, 그 순간 이 판의 규칙이 무너집니다.<br>
      다만 <b>더 잘 변론할 수 있다면 당신의 말로 하셔도 됩니다.</b></p>
    ${items}
  </div>`;
}
